import { type Accessor, createMemo, createSignal } from 'solid-js';
import { type ActionMenuItem } from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { useLogger } from '@trimble/solidjs-utils';
import { asError } from '@trimble/logging';
import { LoadState } from '@trimble/data-loading';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { useConnectProject } from '@trimble/connect-project-providers';
import {
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
} from '@mobile-mapping/providers';
import type { PlanarPaneContent, ViewMode } from '@mobile-mapping/web3d';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import { rotateSdsPanoramas } from './rotate-panoramas';
import { calculateStationHeadingOffset } from './sds-station-mapper';

interface RotatableContext {
  readonly pointCloudId: string;
  readonly planarContent: PlanarPaneContent;
  readonly heading: number;
}

export interface CreateRotatePanoramasActionParams {
  readonly viewModeType: Accessor<ViewMode['type'] | undefined>;
  readonly planarContents: Accessor<readonly PlanarPaneContent[]>;
}

/**
 * Builds the view-tools menu action that rotates the currently displayed
 * SDS-only panorama point cloud's stations by a fixed heading increment
 * (see `rotateSdsPanoramas`). The returned accessor is shaped for
 * `<ViewToolsDropdown>`'s `actions` prop and can be plugged directly into
 * `<ViewModePicker viewToolActions={...} />`. The dropdown
 * only renders when this accessor has items, so the bottom toolbar stays
 * clean in modes that don't have a panorama-rotate action available.
 *
 * The factory reads the wider app, data, capability-registry, connect-project
 * and API-services contexts on its own — callers only need to provide the
 * pane-local view-mode and planar-content accessors.
 *
 * Behavior:
 * - Returns `[]` unless the pane is in Panorama view and the active station's
 *   run maps to an SDS-only point cloud that is fully loaded. The action is
 *   simply absent (not just disabled) in every other case.
 * - The menu label embeds the current heading offset (e.g. "(180°)").
 * - The action is disabled for the duration of an in-flight rotation,
 *   guarded by a local `isRotating` signal. No global loading indicator is
 *   shown — the per-pane texture spinner provides visual feedback during
 *   the panorama reload, and failures surface as a notification toast.
 * - Errors are logged and surface as a generic notification; the optimistic
 *   transform update inside the orchestrator rolls itself back on failure.
 */
export function createRotatePanoramasAction(
  params: CreateRotatePanoramasActionParams
): Accessor<ActionMenuItem[]> {
  const logger = useLogger().getLogger('rotatePanoramas');
  const { scanDataApi } = useApiServices();
  const { selectedRegion } = useConnectProject();
  const { dataState, setDataState } = useDataContext();
  const capabilityRegistry = useCapabilityRegistryContext().capabilityRegistry;

  const [isRotating, setIsRotating] = createSignal(false);

  const rotatableContext = createMemo<RotatableContext | undefined>(() => {
    if (params.viewModeType() !== 'Panorama') return undefined;

    const stationsProvider = capabilityRegistry.get(
      StationsCapabilityProvider
    )();
    const activeRunId = stationsProvider?.activeRunId();
    if (!activeRunId) return undefined;

    const planarContent = params
      .planarContents()
      .find(
        (content) =>
          content.scanDataOnly === true && content.selectedRunId === activeRunId
      );
    if (!planarContent) return undefined;

    const pointCloudId = planarContent.selectedPointCloudTrn;
    if (dataState.pointCloud.state !== LoadState.LoadComplete) return undefined;
    const pointCloud = dataState.pointCloud.items[pointCloudId];
    if (!pointCloud) return undefined;

    return {
      pointCloudId,
      planarContent,
      heading: calculateStationHeadingOffset(pointCloud.stationTransform),
    };
  });

  /**
   * Switching view mode mid-flight is safe: the orchestrator only touches
   * scan-data state and the stations provider, neither of which depends on
   * which view mode is active — the new transform lands regardless and is
   * picked up the next time any pane enters Panorama mode.
   */
  const handleRotate = (context: RotatableContext): void => {
    // Belt-and-braces guard for the brief window between click and reactive
    // disable propagating; the `disabled` flag on the menu item is the
    // primary defense against overlapping rotations.
    if (isRotating()) return;

    const region = selectedRegion();
    const stationsProvider = capabilityRegistry.get(
      StationsCapabilityProvider
    )();
    if (!region || !stationsProvider) return;

    setIsRotating(true);
    void rotateSdsPanoramas({
      pointCloudId: context.pointCloudId,
      planarContent: context.planarContent,
      region,
      scanDataApi,
      stationsProvider,
      dataState,
      setDataState,
    })
      .catch((error) => {
        logger.error(
          'Failed to rotate SDS panoramas',
          { pointCloudId: context.pointCloudId },
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.panoramaActions.rotateFailed'),
        });
      })
      .finally(() => setIsRotating(false));
  };

  const actions = createMemo<ActionMenuItem[]>(() => {
    const context = rotatableContext();
    if (!context) return [];

    return [
      {
        id: 'rotate-panoramas',
        type: 'action',
        label: t('mobileMapping.panoramaActions.rotate', {
          offset: `${context.heading}°`,
        }),
        disabled: isRotating(),
        onClick: () => handleRotate(context),
      },
    ];
  });
  return actions;
}
